import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ShoppingCart, CreditCard, DollarSign, Search, Trash2, Receipt, X, Zap, ScanLine, Bus, Smartphone, Lock, Wrench, Ban, Pencil } from 'lucide-react';
import CambiarPrecioModal from '../components/CambiarPrecioModal';
import { normalizarCodigo } from '../lib/codigoBarras';
import { coincideProducto } from '../lib/buscar';
import { stockApi, busesApi, ventasApi } from '../lib/api';
import { topeVenta } from '../lib/autorizacionKit';
import { ventaPagada } from '../lib/ventas';
import { toast } from 'sonner';
import TicketImprimible from '../components/TicketImprimible';
import VentaRapidaModal from '../components/VentaRapidaModal';
import PagarCreditoModal from '../components/PagarCreditoModal';
import AnularModal from '../components/AnularModal';
import BusSelect from '../components/BusSelect';

const VentasCompleto = () => {
  const [productos, setProductos] = useState<any[]>([]);
  const [ventas, setVentas] = useState<any[]>([]);
  const [buses, setBuses] = useState<any[]>([]);
  const [carritoItems, setCarritoItems] = useState<any[]>([]);
  // Texto que se esta tipeando en el input de cantidad de cada item del carrito (por
  // id). Mientras se edita puede pasar por un valor vacio (ej. al borrar el "1" para
  // escribir "2.5"): si en ese instante se actualizara carritoItems, se interpretaria
  // como cantidad 0 y el item se borraria solo del carrito. Por eso el tipeo se guarda
  // aca y recien se aplica al carrito cuando se termina de editar (blur/Enter).
  const [cantidadEditando, setCantidadEditando] = useState<Record<string, string>>({});
  // Para items "metro": permite tipear la cantidad de ese renglon en centimetros. El
  // carrito (item.cantidad) siempre queda en metros; esto solo cambia como se
  // muestra/interpreta el numero mientras se edita ese item puntual.
  const [cantidadEnCmPorItem, setCantidadEnCmPorItem] = useState<Record<string, boolean>>({});
  const [vistaActual, setVistaActual] = useState<'vender' | 'historial' | 'creditos'>('vender');
  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [tipoPago, setTipoPago] = useState<'contado' | 'credito' | 'mixto' | 'yape'>('contado');
  const [capturasYape, setCapturasYape] = useState<string[]>([]);
  const [mostrarModalCapturaYape, setMostrarModalCapturaYape] = useState(false);
  const [padronBus, setPadronBus] = useState('');
  const [montoEfectivo, setMontoEfectivo] = useState('');
  const [montoCredito, setMontoCredito] = useState('');
  const [modalPago, setModalPago] = useState(false);
  const [ventaParaImprimir, setVentaParaImprimir] = useState<any>(null);
  const [mostrarVentaRapida, setMostrarVentaRapida] = useState(false);
  const [codigoEscaneado, setCodigoEscaneado] = useState('');
  const [mostrarAsignarBus, setMostrarAsignarBus] = useState(false);
  const [busAsignacion, setBusAsignacion] = useState('');
  // Antes esto quedaba fijo en 'efectivo' sin preguntar -- una pieza asignada a un bus para
  // llevar cuenta casi siempre es a crédito de ese bus, así que ese es el default ahora, pero
  // se deja elegir para no perder el caso real de una pieza que sí se cobra al contado.
  const [tipoPagoAsignacion, setTipoPagoAsignacion] = useState<'credito' | 'efectivo'>('credito');
  const [asignando, setAsignando] = useState(false);
  const [busPagoCredito, setBusPagoCredito] = useState<any>(null);

  const [ventaActual, setVentaActual] = useState({
    subtotal: 0,
    igv: 0,
    total: 0,
    descuento: 0,
    observaciones: ''
  });

  const cargarProductos = async () => {
    try {
      const data = await stockApi.getFull();
      setProductos((data || []).filter((p: any) => p.estado === 'activo' && p.tipo !== 'herramienta'));
    } catch (error) {
      console.error('Error cargando productos:', error);
    }
  };

  const cargarBuses = async () => {
    try {
      const data = await busesApi.getAll();
      setBuses((data || []).filter((b: any) => b.estado === 'activo'));
    } catch (error) {
      console.error('Error cargando buses:', error);
    }
  };

  useEffect(() => {
    cargarProductos();
    cargarVentasDesdeSupabase();
    cargarBuses();

    // Sincronización periódica (ya no hay suscripción realtime de Supabase)
    const intervalo = setInterval(() => {
      cargarProductos();
      cargarVentasDesdeSupabase();
      cargarBuses();
    }, 30000);

    return () => clearInterval(intervalo);
  }, []);



  // Calcular totales del carrito
  useEffect(() => {
    // El precio del stock ya es el precio final: no se agrega IGV ni ningun otro cargo.
    const subtotal = carritoItems.reduce((sum, item) => sum + (item.precio * item.cantidad), 0);
    const igv = 0;
    const total = subtotal - ventaActual.descuento;
    
    setVentaActual(prev => ({
      ...prev,
      subtotal,
      igv,
      total
    }));
  }, [carritoItems, ventaActual.descuento]);

  // Imprime el ticket termico apenas React termina de renderizarlo (fuera de pantalla)
  useEffect(() => {
    if (ventaParaImprimir) {
      const timer = setTimeout(() => {
        window.print();
        setVentaParaImprimir(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [ventaParaImprimir]);

  const cargarVentasDesdeSupabase = async () => {
    try {
      const data = await ventasApi.getAll();
      if (data) setVentas(data);
    } catch (error) {
      console.error('Error cargando ventas:', error);
    }
  };

  // Buscar productos por nombre o código. El codigo tambien se compara normalizado
  // (ver lib/codigoBarras.ts) porque algunos lectores de codigo de barras escriben
  // apostrofe en vez de guion segun su configuracion de teclado.
  const productosFiltrados = productos.filter(p => {
    return coincideProducto(busquedaProducto, p);
  });

  // Agregar producto al carrito
  const agregarAlCarrito = (producto: any) => {
    if (topeVenta(producto) <= 0) {
      alert('Producto sin stock');
      return;
    }

    const itemExistente = carritoItems.find(item => item.id === producto.id);
    if (itemExistente) {
      setCarritoItems(carritoItems.map(item =>
        item.id === producto.id
          ? { ...item, cantidad: Math.min(item.cantidad + 1, topeVenta(producto)) }
          : item
      ));
    } else {
      // Normalizar el precio: usar precio_venta o precio
      const productoNormalizado = {
        ...producto,
        precio: producto.precio_venta || producto.precio || 0,
        precio_lista: Number(producto.precio_venta || producto.precio || 0),
        cantidad: 1
      };
      setCarritoItems([...carritoItems, productoNormalizado]);
    }
  };

  // El lector de codigo de barras funciona como teclado: escribe el codigo y
  // presiona Enter solo. Si encuentra el producto lo agrega directo al carrito.
  const manejarEscaneoVenta = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    const codigo = codigoEscaneado.trim();
    if (!codigo) return;
    const encontrado = productos.find(p => normalizarCodigo(p.codigo) === normalizarCodigo(codigo));
    if (encontrado) {
      agregarAlCarrito(encontrado);
      toast.success(`${encontrado.nombre} agregado al carrito`);
    } else {
      toast.error('No se encontró ningún producto con ese código');
    }
    setCodigoEscaneado('');
  };

  // Actualizar cantidad en carrito
  const actualizarCantidad = (id: string, cantidad: number) => {
    const producto = productos.find(p => p.id === id);
    if (cantidad <= 0) {
      setCarritoItems(carritoItems.filter(item => item.id !== id));
    } else if (!producto) {
      // Item sin producto de stock detras (ej. un servicio manual) - no hay tope de stock.
      setCarritoItems(carritoItems.map(item =>
        item.id === id ? { ...item, cantidad } : item
      ));
    } else if (cantidad <= topeVenta(producto)) {
      setCarritoItems(carritoItems.map(item =>
        item.id === id ? { ...item, cantidad } : item
      ));
    }
    setCantidadEditando(prev => {
      const { [id]: _omitido, ...resto } = prev;
      return resto;
    });
  };

  // Servicio manual: costo independiente de mantenimientos, sin producto de stock
  // detras (ej. lavado, mano de obra). Se agrega como un item mas del carrito (con
  // id sintetico unico para poder distinguirlo/actualizarlo en el carrito) y en el
  // backend, cuando la venta se registra con caja cerrada, ese monto entra a Caja
  // Ventas bajo la categoria "Servicios" en vez de "Ventas".
  // 2026-10-22: cambio de precio en el carrito y descuento -> clave de administrador
  const [precioEditando, setPrecioEditando] = useState<any>(null);
  const [autorizacionPrecio, setAutorizacionPrecio] = useState<{ clave: string; motivo: string } | null>(null);
  const [autorizandoDescuento, setAutorizandoDescuento] = useState(false);
  const [mostrarFormServicio, setMostrarFormServicio] = useState(false);
  const [servicioDescripcion, setServicioDescripcion] = useState('');
  const [servicioMonto, setServicioMonto] = useState('');

  const agregarServicio = () => {
    const monto = parseFloat(servicioMonto) || 0;
    if (!servicioDescripcion.trim()) {
      toast.error('Describe el servicio');
      return;
    }
    if (monto <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    setCarritoItems([...carritoItems, {
      id: `srv-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      nombre: `Servicio: ${servicioDescripcion.trim()}`,
      precio: monto,
      cantidad: 1,
      es_servicio: true,
      unidad: 'unidad'
    }]);
    setServicioDescripcion('');
    setServicioMonto('');
    setMostrarFormServicio(false);
    toast.success('Servicio agregado al carrito');
  };

  // Se llama al terminar de editar el input de cantidad (blur o Enter). Si lo que
  // quedo escrito no es un numero valido, se descarta el cambio y se vuelve al valor
  // que tenia antes, en vez de interpretarlo como 0 y borrar el producto del carrito.
  const confirmarCantidadTexto = (id: string) => {
    const texto = cantidadEditando[id];
    if (texto === undefined) return;
    const parsed = parseFloat(texto);
    if (!isNaN(parsed) && parsed >= 0) {
      const factor = cantidadEnCmPorItem[id] ? 100 : 1;
      actualizarCantidad(id, parsed / factor);
    } else {
      setCantidadEditando(prev => {
        const { [id]: _omitido, ...resto } = prev;
        return resto;
      });
    }
  };

  // Eliminar del carrito
  const eliminarDelCarrito = (id: string) => {
    setCarritoItems(carritoItems.filter(item => item.id !== id));
  };

  // Capturas de pago de Yape: se leen como base64 y se mandan asi al backend,
  // que las guarda como archivos y las liga a la venta recien creada.
  const agregarCapturasYape = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      const lector = new FileReader();
      lector.onload = () => setCapturasYape(prev => [...prev, lector.result as string]);
      lector.readAsDataURL(file);
    });
  };

  const quitarCapturaYape = (index: number) => {
    setCapturasYape(prev => prev.filter((_, i) => i !== index));
  };

  // Subida de comprobante de Yape DESPUES de la venta (desde el Historial): la captura
  // ya no es obligatoria al vender, asi que esta es la forma de completarla mas tarde.
  const [ventaComprobante, setVentaComprobante] = useState<any>(null);
  const [capturasNuevasComprobante, setCapturasNuevasComprobante] = useState<string[]>([]);
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);

  // Cambiar tipo de pago de una venta ya cerrada (contado <-> credito), con clave de
  // administrador -- ver POST /api/ventas/:id/cambiar-tipo-pago.
  const [ventaCambiarTipo, setVentaCambiarTipo] = useState<any>(null);
  const [nuevoTipoPago, setNuevoTipoPago] = useState<'efectivo' | 'credito' | 'yape'>('efectivo');
  const [padronCambioTipo, setPadronCambioTipo] = useState('');
  const [claveCambioTipo, setClaveCambioTipo] = useState('');
  const [enviandoCambioTipo, setEnviandoCambioTipo] = useState(false);

  const abrirCambiarTipo = (venta: any) => {
    setVentaCambiarTipo(venta);
    setNuevoTipoPago(venta.tipo_pago === 'credito' ? 'efectivo' : 'credito');
    setPadronCambioTipo(venta.placa_bus || '');
    setClaveCambioTipo('');
  };

  const confirmarCambioTipo = async () => {
    if (!ventaCambiarTipo) return;
    if (!claveCambioTipo) {
      toast.error('Ingresa la clave de administrador');
      return;
    }
    if (nuevoTipoPago === 'credito' && !padronCambioTipo) {
      toast.error('Selecciona el bus/padrón para pasar la venta a crédito');
      return;
    }
    setEnviandoCambioTipo(true);
    try {
      const actualizada = await ventasApi.cambiarTipoPago(ventaCambiarTipo.id, {
        clave: claveCambioTipo,
        nuevo_tipo_pago: nuevoTipoPago,
        placa_bus: padronCambioTipo || undefined
      });
      setVentas(prev => prev.map(v => (v.id === actualizada.id ? actualizada : v)));
      const etiquetaNuevoTipo = nuevoTipoPago === 'credito' ? 'crédito' : nuevoTipoPago === 'yape' ? 'Yape' : 'contado';
      toast.success(`Venta ${actualizada.numero} ahora es ${etiquetaNuevoTipo}`);
      setVentaCambiarTipo(null);
      await cargarBuses();
    } catch (error: any) {
      toast.error('Error al cambiar el tipo de pago: ' + error.message);
    } finally {
      setEnviandoCambioTipo(false);
    }
  };

  const [ventaCambiarBus, setVentaCambiarBus] = useState<any>(null);
  // Anular una venta mal registrada (producto/cantidad/precio equivocado)
  const [ventaParaAnular, setVentaParaAnular] = useState<any>(null);
  const [nuevoBusCambio, setNuevoBusCambio] = useState('');
  const [claveCambioBus, setClaveCambioBus] = useState('');
  const [enviandoCambioBus, setEnviandoCambioBus] = useState(false);

  const abrirCambiarBus = (venta: any) => {
    setVentaCambiarBus(venta);
    setNuevoBusCambio('');
    setClaveCambioBus('');
  };

  const confirmarCambioBus = async () => {
    if (!ventaCambiarBus) return;
    if (!claveCambioBus) {
      toast.error('Ingresa la clave de administrador');
      return;
    }
    if (!nuevoBusCambio) {
      toast.error('Selecciona el bus destino');
      return;
    }
    setEnviandoCambioBus(true);
    try {
      const actualizada = await ventasApi.cambiarBus(ventaCambiarBus.id, {
        clave: claveCambioBus,
        nueva_placa_bus: nuevoBusCambio
      });
      setVentas(prev => prev.map(v => (v.id === actualizada.id ? actualizada : v)));
      toast.success(ventaCambiarBus.placa_bus ? `Venta ${actualizada.numero} pasada del bus ${ventaCambiarBus.placa_bus} al bus ${nuevoBusCambio}` : `Venta ${actualizada.numero} asignada al bus ${nuevoBusCambio}`);
      setVentaCambiarBus(null);
      await cargarBuses();
    } catch (error: any) {
      toast.error('Error al cambiar el bus: ' + error.message);
    } finally {
      setEnviandoCambioBus(false);
    }
  };

  const agregarCapturasComprobante = (files: FileList | null) => {
    if (!files) return;
    Array.from(files).forEach(file => {
      const lector = new FileReader();
      lector.onload = () => setCapturasNuevasComprobante(prev => [...prev, lector.result as string]);
      lector.readAsDataURL(file);
    });
  };

  const quitarCapturaNuevaComprobante = (index: number) => {
    setCapturasNuevasComprobante(prev => prev.filter((_, i) => i !== index));
  };

  const subirComprobante = async () => {
    if (!ventaComprobante || capturasNuevasComprobante.length === 0) return;
    setSubiendoComprobante(true);
    try {
      const { yape_capturas } = await ventasApi.subirCapturasYape(ventaComprobante.id, capturasNuevasComprobante);
      setVentas(prev => prev.map(v => (v.id === ventaComprobante.id ? { ...v, yape_capturas } : v)));
      setVentaComprobante((prev: any) => (prev ? { ...prev, yape_capturas } : prev));
      setCapturasNuevasComprobante([]);
      toast.success('Comprobante subido');
    } catch (error: any) {
      toast.error('Error al subir comprobante: ' + error.message);
    } finally {
      setSubiendoComprobante(false);
    }
  };

  // Procesar venta
  const procesarVenta = async () => {
    if (carritoItems.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    // El credito es del bus (por padron), no de una persona: alcanza con el padron.
    if ((tipoPago === 'credito' || tipoPago === 'mixto') && !padronBus) {
      toast.error("El padrón del bus es obligatorio para ventas a crédito o mixtas");
      return;
    }

    // Validar montos para venta mixta
    if (tipoPago === 'mixto') {
      const efectivo = parseFloat(montoEfectivo) || 0;
      const credito = parseFloat(montoCredito) || 0;
      const totalVenta = ventaActual.total;

      if (efectivo <= 0 || credito <= 0) {
        toast.error('Ambos montos deben ser mayores a cero en venta mixta');
        return;
      }

      if (Math.abs((efectivo + credito) - totalVenta) > 0.01) {
        toast.error(`La suma de efectivo (S/ ${efectivo.toFixed(2)}) y crédito (S/ ${credito.toFixed(2)}) debe ser igual al total (S/ ${totalVenta.toFixed(2)})`);
        return;
      }
    }

    // La captura de Yape es opcional: si no se adjunta al momento de la venta,
    // se puede subir despues desde el Historial (boton "Comprobante").

    // 2026-10-22: el descuento necesita clave de administrador
    if (ventaActual.descuento > 0 && !autorizacionPrecio) {
      setAutorizandoDescuento(true);
      return;
    }

    let venta: any;
    try {
      venta = await ventasApi.crearConCarrito({
        cliente_nombre: 'Cliente Contado',
        placa_bus: padronBus || null,
        tipo_pago: tipoPago === 'contado' ? 'efectivo' : tipoPago,
        items: carritoItems,
        subtotal: ventaActual.subtotal,
        igv: ventaActual.igv,
        descuento: ventaActual.descuento,
        total: ventaActual.total,
        monto_credito: tipoPago === 'mixto' ? parseFloat(montoCredito) : undefined,
        yape_capturas: tipoPago === 'yape' ? capturasYape : undefined,
        autorizacion_precio: autorizacionPrecio || undefined,
        cerrada: true, // Procesar Venta siempre cierra e imprime al toque
        observaciones: `${ventaActual.observaciones}${tipoPago === 'mixto' ? ` - MIXTO: Efectivo S/${montoEfectivo} + Crédito S/${montoCredito}` : ''}`
      });
      toast.success('Venta guardada');
      setVentaParaImprimir(venta);
      await Promise.all([cargarProductos(), cargarBuses()]);
    } catch (error: any) {
      console.error('Error guardando venta:', error);
      toast.error('Error al guardar venta: ' + error.message);
      return;
    }

    // 2026-11-02: aquí se pedía un "archivo de texto" a /api/ventas/txt, ruta que el servidor no
    // tiene (siempre 404 -> aviso falso "comprobante no generado"). El comprobante de cada venta ya
    // lo guarda el servidor en PDF (guardarPdfRegistro) y el ticket se imprime aparte.
    // Limpiar carrito
    setCarritoItems([]);
    setTipoPago('contado');
    setPadronBus('');
    setMontoEfectivo('');
    setMontoCredito('');
    setCapturasYape([]);
    setVentaActual(prev => ({ ...prev, descuento: 0, observaciones: '' })); setAutorizacionPrecio(null);
    setTipoPago('contado');
    setPadronBus('');
    setVentaActual(prev => ({ ...prev, descuento: 0, observaciones: '' })); setAutorizacionPrecio(null);
    setModalPago(false);

    toast.success(`Venta ${venta.numero} procesada y sincronizada`);
  };

  // Asigna el carrito actual a un bus como piezas pendientes (sin imprimir).
  // Una venta por cada producto, igual que "Agregar Piezas" en Buses, para que
  // se puedan cerrar e imprimir pieza por pieza desde "Piezas Usadas".
  const asignarCarritoABus = async () => {
    if (carritoItems.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }
    if (!busAsignacion) {
      toast.error('Selecciona el bus');
      return;
    }

    setAsignando(true);
    try {
      for (const item of carritoItems) {
        const subtotal = item.precio * item.cantidad;
        const igv = 0;
        await ventasApi.crearConCarrito({
          cliente_nombre: 'Cliente Contado',
          placa_bus: busAsignacion,
          tipo_pago: tipoPagoAsignacion,
          items: [item],
          subtotal,
          igv,
          total: subtotal,
          autorizacion_precio: autorizacionPrecio || undefined,
          // Al contado el cobro ya se hizo en el momento de asignar la pieza, así que
          // entra a Caja Central de una vez (cerrada=true) -- antes quedaba siempre
          // pendiente igual que a crédito, y el dinero ya cobrado no aparecía en Caja
          // hasta que alguien lo cerrara manualmente después desde "Piezas Usadas".
          // A crédito sigue quedando pendiente: ahí sí no se cobró nada todavía.
          cerrada: tipoPagoAsignacion === 'efectivo',
          observaciones: ventaActual.observaciones ||
            (tipoPagoAsignacion === 'efectivo' ? 'Pieza asignada al bus, cobrada al contado' : 'Pieza pendiente asignada al bus')
        });
      }
      const etiquetaTipo = tipoPagoAsignacion === 'credito' ? 'a crédito' : 'al contado';
      const sufijoEstado = tipoPagoAsignacion === 'credito' ? ', pendientes de cierre' : ', ya registradas en Caja';
      toast.success(`${carritoItems.length} pieza(s) asignada(s) al bus ${busAsignacion} (${etiquetaTipo})${sufijoEstado}`);
      setCarritoItems([]);
      setVentaActual(prev => ({ ...prev, descuento: 0, observaciones: '' })); setAutorizacionPrecio(null);
      setMostrarAsignarBus(false);
      setBusAsignacion('');
      setTipoPagoAsignacion('credito');
      await cargarProductos();
      await cargarBuses();
    } catch (error: any) {
      console.error('Error asignando carrito a bus:', error);
      toast.error('Error al asignar piezas al bus: ' + error.message);
    } finally {
      setAsignando(false);
    }
  };

  const getStockColor = (stock: number) => {
    if (stock <= 5) return 'text-red-600 bg-red-50';
    if (stock <= 15) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const ventasHoy = ventas.filter(v => 
    new Date(v.fecha).toDateString() === new Date().toDateString()
  );

  // Credito pendiente = lo que de verdad falta cobrar (saldo de la venta), no ventas.estado,
  // que nunca cambia al pagarse (por eso seguia mostrando 445 despues del reinicio de deudas).
  const saldoVenta = (v: any) => ((Number(v.saldo_credito_productos) || 0) + (Number(v.saldo_credito_servicios) || 0));
  const ventasCredito = ventas.filter(v => (v.tipo_pago === 'credito' || v.tipo_pago === 'mixto') && saldoVenta(v) > 0.004);

  const totalVentasHoy = ventasHoy.reduce((sum, v) => sum + v.total, 0);

  // Filtrar ventas por rango de fechas
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Sistema de Ventas</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setMostrarVentaRapida(true)}
            className="px-4 py-2 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-white flex items-center gap-2"
          >
            <Zap size={16} />
            Venta Rápida
          </button>
          <Link
            to="/ventas/retroactiva"
            className="px-4 py-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white flex items-center gap-2 text-sm"
            title="Carga administrativa de ventas pasadas"
          >
            <Lock size={14} />
            Venta Pasada
          </Link>
          <button
            onClick={() => setVistaActual('vender')}
            className={`px-4 py-2 rounded-lg ${vistaActual === 'vender' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Vender
          </button>
          <button
            onClick={() => setVistaActual('historial')}
            className={`px-4 py-2 rounded-lg ${vistaActual === 'historial' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Historial
          </button>
          <button
            onClick={() => setVistaActual('creditos')}
            className={`px-4 py-2 rounded-lg ${vistaActual === 'creditos' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
          >
            Créditos
          </button>
        </div>
      </div>

      {/* Vista de Vender */}
      {vistaActual === 'vender' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Panel de productos */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h2 className="text-lg font-semibold mb-4">Productos</h2>
              
              {/* Buscar producto */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Buscar Producto</label>
                <div className="flex">
                  <input
                    type="text"
                    value={busquedaProducto}
                    onChange={(e) => setBusquedaProducto(e.target.value)}
                    placeholder="Nombre del producto"
                    className="flex-1 p-2 border rounded-l-lg"
                  />
                  <button className="bg-gray-600 text-white px-4 py-2 rounded-r-lg">
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Servicio manual: costo aparte, sin producto de stock */}
              <div className="mb-4 border border-orange-200 bg-orange-50 rounded-lg p-3">
                {!mostrarFormServicio ? (
                  <button
                    onClick={() => setMostrarFormServicio(true)}
                    className="w-full flex items-center justify-center gap-2 text-orange-700 font-medium text-sm py-1"
                  >
                    <Wrench className="w-4 h-4" />
                    + Agregar servicio (costo manual, sin stock)
                  </button>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-orange-800 flex items-center gap-1">
                      <Wrench className="w-4 h-4" /> Servicio manual
                    </p>
                    <input
                      type="text"
                      value={servicioDescripcion}
                      onChange={(e) => setServicioDescripcion(e.target.value)}
                      placeholder="Descripción del servicio"
                      className="w-full p-2 border rounded text-sm"
                    />
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={servicioMonto}
                      onChange={(e) => setServicioMonto(e.target.value)}
                      placeholder="Monto (S/)"
                      className="w-full p-2 border rounded text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={agregarServicio}
                        className="flex-1 bg-orange-600 text-white py-1.5 rounded text-sm font-medium hover:bg-orange-700"
                      >
                        Agregar al carrito
                      </button>
                      <button
                        onClick={() => { setMostrarFormServicio(false); setServicioDescripcion(''); setServicioMonto(''); }}
                        className="px-3 bg-gray-200 rounded text-sm"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Escanear código de barras */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 flex items-center gap-1">
                  <ScanLine className="w-4 h-4 text-blue-500" />
                  Escanear Código
                </label>
                <input
                  type="text"
                  value={codigoEscaneado}
                  onChange={(e) => setCodigoEscaneado(e.target.value)}
                  onKeyDown={manejarEscaneoVenta}
                  placeholder="Escanea el código del repuesto..."
                  className="w-full p-2 border-2 border-blue-200 rounded-lg focus:border-blue-500"
                />
              </div>

              {/* Lista de productos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {productosFiltrados.slice(0, 8).map((producto) => (
                  <div key={producto.id} className="border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium">{producto.nombre}{producto.es_kit && <span className="ml-2 px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 text-xs font-semibold align-middle">KIT</span>}</h3>
                        <p className="text-sm text-gray-600">{producto.descripcion}</p>
                        {producto.codigo && <p className="text-xs text-gray-500">Código: {producto.codigo}</p>}
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-lg">S/ {(producto.precio_venta || producto.precio || 0).toFixed(2)}</p>
                        <span className={`px-2 py-1 rounded text-xs ${getStockColor(producto.stock)}`}>
                          {producto.es_kit ? 'Kits armables' : 'Stock'}: {producto.stock}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => agregarAlCarrito(producto)}
                      disabled={topeVenta(producto) <= 0}
                      className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Panel del carrito */}
          <div>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Carrito de Compras</h2>
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>

              {carritoItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Carrito vacío</p>
              ) : (
                <>
                  <div className="space-y-4 mb-4">
                    {carritoItems.map((item) => (
                      <div key={item.id} className="flex justify-between items-center border-b pb-2">
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">
                            {item.es_servicio && <Wrench className="w-3 h-3 inline mr-1 text-orange-600" />}
                            {item.nombre}
                          </h4>
                          {item.es_servicio ? (
                            <p className="text-xs text-gray-600">S/ {(item.precio || 0).toFixed(2)} c/u</p>
                          ) : (
                            <button type="button" onClick={() => setPrecioEditando(item)} title="Cambiar precio"
                                    className="text-xs text-gray-600 hover:text-blue-700 flex items-center gap-1">
                              {Number(item.precio_lista) > 0 && Math.abs((item.precio || 0) - Number(item.precio_lista)) > 0.005 && (
                                <span className="line-through text-gray-400">S/ {Number(item.precio_lista).toFixed(2)}</span>
                              )}
                              <span className={Number(item.precio_lista) > 0 && Math.abs((item.precio || 0) - Number(item.precio_lista)) > 0.005 ? 'text-blue-700 font-semibold' : ''}>
                                S/ {(item.precio || 0).toFixed(2)} c/u
                              </span>
                              <Pencil className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                        {item.es_servicio ? (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">Servicio</span>
                            <button
                              onClick={() => eliminarDelCarrito(item.id)}
                              className="text-red-600 ml-2"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => actualizarCantidad(item.id, item.cantidad - 1)}
                              className="bg-gray-200 w-6 h-6 rounded flex items-center justify-center"
                            >
                              -
                            </button>
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={cantidadEditando[item.id] ?? (cantidadEnCmPorItem[item.id] ? item.cantidad * 100 : item.cantidad)}
                              onChange={(e) => setCantidadEditando(prev => ({ ...prev, [item.id]: e.target.value }))}
                              onBlur={() => confirmarCantidadTexto(item.id)}
                              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                              className="w-16 text-center border rounded"
                              title={item.unidad && item.unidad !== 'unidad' ? `Cantidad en ${cantidadEnCmPorItem[item.id] ? 'cm' : item.unidad}` : undefined}
                            />
                            {item.unidad === 'metro' ? (
                              <button
                                type="button"
                                onClick={() => {
                                  setCantidadEnCmPorItem(prev => ({ ...prev, [item.id]: !prev[item.id] }));
                                  setCantidadEditando(prev => { const { [item.id]: _o, ...resto } = prev; return resto; });
                                }}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                {cantidadEnCmPorItem[item.id] ? 'cm' : 'm'}
                              </button>
                            ) : item.unidad && item.unidad !== 'unidad' && (
                              <span className="text-xs text-gray-500">{item.unidad}</span>
                            )}
                            <button
                              onClick={() => actualizarCantidad(item.id, item.cantidad + 1)}
                              className="bg-gray-200 w-6 h-6 rounded flex items-center justify-center"
                            >
                              +
                            </button>
                            <button
                              onClick={() => eliminarDelCarrito(item.id)}
                              className="text-red-600 ml-2"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Totales */}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>S/ {ventaActual.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>S/ {ventaActual.total.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-2 mt-4">
                    <button
                      onClick={() => setModalPago(true)}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center"
                    >
                      <Receipt className="w-5 h-5 mr-2" />
                      Procesar Venta
                    </button>
                    <button
                      onClick={() => setMostrarAsignarBus(true)}
                      className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-700 flex items-center justify-center"
                    >
                      <Bus className="w-5 h-5 mr-2" />
                      Asignar a Bus (pendiente)
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Estadísticas rápidas */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold mb-4">Resumen del Día</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Ventas Hoy:</span>
                  <span className="font-bold">{ventasHoy.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total Vendido:</span>
                  <span className="font-bold">S/ {totalVentasHoy.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Créditos Pendientes:</span>
                  <span className="font-bold text-red-600">{ventasCredito.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vista de Historial */}
      {vistaActual === 'historial' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Historial de Ventas</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left">Número</th>
                  <th className="px-4 py-2 text-left">Fecha</th>
                  <th className="px-4 py-2 text-left">Producto Vendido</th>
                  <th className="px-4 py-2 text-left">Cliente</th>
                  <th className="px-4 py-2 text-right">Total</th>
                  <th className="px-4 py-2 text-center">Pago</th>
                  <th className="px-4 py-2 text-center">Estado</th>
                  <th className="px-4 py-2 text-center">Comprobante</th>
                  <th className="px-4 py-2 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ventas.map((venta) => (
                  <tr key={venta.id} className="border-b">
                    <td className="px-4 py-2 font-mono">{venta.numero}</td>
                    <td className="px-4 py-2">{new Date(venta.fecha).toLocaleDateString('es-PE')}</td>
                    <td className="px-4 py-2">
                      <span className="font-semibold text-blue-900">
                        {venta.items && Array.isArray(venta.items) && venta.items.length > 0
                          ? venta.items.map((item: any) => item.nombre || item.producto_nombre || item.name).join(', ')
                          : 'Sin productos'}
                      </span>
                    </td>
                    <td className="px-4 py-2">{venta.cliente_nombre || 'Cliente Contado'}{!venta.placa_bus && <span className="block text-[10px] text-gray-400">sin padrón</span>}</td>
                    <td className="px-4 py-2 text-right font-bold">S/ {venta.total.toFixed(2)}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        venta.tipo_pago === 'yape' ? 'bg-purple-100 text-purple-800' :
                        venta.tipo_pago === 'credito' ? 'bg-orange-100 text-orange-800' :
                        venta.tipo_pago === 'mixto' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {venta.tipo_pago === 'yape' ? 'YAPE' :
                         venta.tipo_pago === 'credito' ? 'CRÉDITO' :
                         venta.tipo_pago === 'mixto' ? 'MIXTO' : 'CONTADO'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        ventaPagada(venta) ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {ventaPagada(venta) ? 'PAGADO' : 'PENDIENTE'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => setVentaComprobante(venta)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100"
                        title="Comprobante de pago (opcional)"
                      >
                        <Smartphone className="w-3.5 h-3.5" />
                        {Array.isArray(venta.yape_capturas) && venta.yape_capturas.length > 0
                          ? `Ver (${venta.yape_capturas.length})`
                          : 'Subir'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => abrirCambiarTipo(venta)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1.5 bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-lg hover:bg-cyan-100"
                        title={venta.cerrada
                          ? 'Cambiar entre contado y crédito (requiere clave de administrador)'
                          : 'Venta abierta (pieza pendiente): solo se ajusta la deuda del bus, no Caja Central'}
                      >
                        <Lock className="w-3.5 h-3.5" />
                        Cambiar tipo
                      </button>
                      {!venta.cerrada && (
                        <div className="text-[10px] text-gray-400 mt-0.5">Pieza pendiente</div>
                      )}
                      {(venta.placa_bus || ventaPagada(venta)) && (
                        <button
                          type="button"
                          onClick={() => abrirCambiarBus(venta)}
                          className="inline-flex items-center gap-1 text-xs px-2 py-1.5 mt-1 bg-teal-50 text-teal-700 border border-teal-200 rounded-lg hover:bg-teal-100"
                          title={venta.placa_bus ? 'Mover esta venta a otro bus (se eligió el bus equivocado) — requiere clave de administrador' : 'Asignar un bus a esta venta hecha sin padrón — requiere clave de administrador'}
                        >
                          <Bus className="w-3.5 h-3.5" />
                          {venta.placa_bus ? 'Cambiar bus' : 'Asignar bus'}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setVentaParaAnular(venta)}
                        className="inline-flex items-center gap-1 text-xs px-2 py-1.5 mt-1 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100"
                        title="Anular esta venta (se registró mal): devuelve stock, deuda y caja — requiere clave de administrador"
                      >
                        <Ban className="w-3.5 h-3.5" />
                        Anular
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vista de Créditos */}
      {vistaActual === 'creditos' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Gestión de Créditos</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Buses con deuda: el credito es del bus, no de una persona */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h3 className="font-semibold">Buses con Crédito</h3>
              </div>
              <div className="p-4 space-y-4">
                {buses.filter(b => (b.credito_usado || 0) > 0).length === 0 && (
                  <p className="text-sm text-gray-500">Ningún bus tiene deuda pendiente.</p>
                )}
                {buses.filter(b => (b.credito_usado || 0) > 0).map((bus) => (
                  <div key={bus.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">
                          Bus <span className="bg-blue-100 px-2 py-1 rounded text-xs font-mono">{bus.padron}</span>
                        </h4>
                        <p className="text-sm text-gray-600">{bus.marca} {bus.modelo}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-red-600 mb-2">Deuda: S/ {(bus.credito_usado || 0).toFixed(2)}</p>
                        <button
                          onClick={() => setBusPagoCredito(bus)}
                          className="text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                        >
                          Pagar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ventas a crédito pendientes */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h3 className="font-semibold">Créditos Pendientes</h3>
              </div>
              <div className="p-4 space-y-4">
                {ventasCredito.map((venta) => (
                  <div key={venta.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium">{venta.numero}</h4>
                        <p className="text-sm text-gray-600">{new Date(venta.fecha).toLocaleDateString('es-PE')}</p>
                        {venta.placa_bus && (
                          <p className="text-sm text-blue-600">
                            Bus: <span className="bg-blue-100 px-2 py-1 rounded text-xs font-mono">{venta.placa_bus}</span>
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="font-bold">S/ {venta.total.toFixed(2)}</p>
                        <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800">
                          Debe S/ {saldoVenta(venta).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Pago */}
      {modalPago && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Procesar Venta</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Tipo de Pago</label>
                <div className="grid grid-cols-4 gap-2">
                  <button
                    onClick={() => {
                      setTipoPago('contado');
                      setMontoEfectivo('');
                      setMontoCredito('');
                      setCapturasYape([]);
                    }}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center justify-center ${
                      tipoPago === 'contado' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    <DollarSign className="w-5 h-5 mb-1" />
                    <span className="text-xs">Contado</span>
                  </button>
                  <button
                    onClick={() => {
                      setTipoPago('credito');
                      setMontoEfectivo('');
                      setMontoCredito('');
                      setCapturasYape([]);
                    }}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center justify-center ${
                      tipoPago === 'credito' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    <CreditCard className="w-5 h-5 mb-1" />
                    <span className="text-xs">Crédito</span>
                  </button>
                  <button
                    onClick={() => {
                      setTipoPago('mixto');
                      setMontoEfectivo('');
                      setMontoCredito('');
                      setCapturasYape([]);
                    }}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center justify-center ${
                      tipoPago === 'mixto' ? 'border-blue-600 bg-blue-50' : 'border-gray-300'
                    }`}
                  >
                    <div className="flex items-center mb-1">
                      <DollarSign className="w-4 h-4" />
                      <CreditCard className="w-4 h-4" />
                    </div>
                    <span className="text-xs">Mixto</span>
                  </button>
                  <button
                    onClick={() => {
                      setTipoPago('yape');
                      setMontoEfectivo('');
                      setMontoCredito('');
                    }}
                    className={`p-3 rounded-lg border-2 flex flex-col items-center justify-center ${
                      tipoPago === 'yape' ? 'border-purple-600 bg-purple-50' : 'border-gray-300'
                    }`}
                  >
                    <Smartphone className="w-5 h-5 mb-1" />
                    <span className="text-xs">Yape</span>
                  </button>
                </div>
              </div>

              {tipoPago === 'yape' && (
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-purple-800">Comprobante de Yape</h4>
                    <button
                      type="button"
                      onClick={() => setMostrarModalCapturaYape(true)}
                      className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      + Agregar captura
                    </button>
                  </div>
                  {capturasYape.length === 0 ? (
                    <p className="text-xs text-purple-700">
                      Sin capturas todavía. Es opcional: podés confirmar la venta sin adjuntarla ahora y subirla después desde el Historial.
                    </p>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {capturasYape.map((captura, i) => (
                        <div key={i} className="relative">
                          <img src={captura} alt={`Captura ${i + 1}`} className="w-full h-16 object-cover rounded border" />
                          <button
                            type="button"
                            onClick={() => quitarCapturaYape(i)}
                            className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {(tipoPago === 'credito' || tipoPago === 'mixto') && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-sm text-blue-800">
                  {padronBus ? (
                    <>El crédito queda a nombre del bus <strong>{padronBus}</strong>. Deuda actual: S/ {(buses.find(b => b.padron === padronBus)?.credito_usado || 0).toFixed(2)}</>
                  ) : (
                    'Seleccioná el bus/padrón abajo: el crédito se registra a su nombre, no al de una persona.'
                  )}
                </div>
              )}

              {/* Campo Padrón del Bus */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Bus / Padrón <span className="text-red-500">*</span>
                  </label>
                  <BusSelect buses={buses} value={padronBus} onChange={(v) => setPadronBus(v)} className="w-full p-2 border rounded" />
                </div>

              {/* Montos para venta mixta */}
              {tipoPago === 'mixto' && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="text-sm font-semibold mb-3 text-yellow-800">Distribución de Pago Mixto</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1">Monto Efectivo (S/) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={montoEfectivo}
                        onChange={(e) => {
                          const efectivo = parseFloat(e.target.value) || 0;
                          setMontoEfectivo(e.target.value);
                          // Auto-calcular crédito
                          const credito = Math.max(0, ventaActual.total - efectivo);
                          setMontoCredito(credito.toFixed(2));
                        }}
                        className="w-full p-2 border rounded"
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Monto a Crédito (S/) *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={montoCredito}
                        onChange={(e) => {
                          const credito = parseFloat(e.target.value) || 0;
                          setMontoCredito(e.target.value);
                          // Auto-calcular efectivo
                          const efectivo = Math.max(0, ventaActual.total - credito);
                          setMontoEfectivo(efectivo.toFixed(2));
                        }}
                        className="w-full p-2 border rounded"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="pt-2 border-t border-yellow-300">
                      <div className="flex justify-between text-sm">
                        <span>Total Venta:</span>
                        <span className="font-semibold">S/ {ventaActual.total.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm mt-1">
                        <span>Suma (Efectivo + Crédito):</span>
                        <span className={`font-semibold ${
                          Math.abs((parseFloat(montoEfectivo) || 0) + (parseFloat(montoCredito) || 0) - ventaActual.total) < 0.01
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          S/ {((parseFloat(montoEfectivo) || 0) + (parseFloat(montoCredito) || 0)).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Descuento (S/)</label>
                <input
                  type="number"
                  step="0.01"
                  value={ventaActual.descuento}
                  onChange={(e) => setVentaActual(prev => ({ ...prev, descuento: parseFloat(e.target.value) || 0 }))}
                  className="w-full p-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Observaciones</label>
                <textarea
                  value={ventaActual.observaciones}
                  onChange={(e) => setVentaActual(prev => ({ ...prev, observaciones: e.target.value }))}
                  className="w-full p-2 border rounded"
                  rows={2}
                />
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total a Pagar:</span>
                  <span>S/ {ventaActual.total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setModalPago(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancelar
              </button>
              <button
                onClick={procesarVenta}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Confirmar Venta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para agregar capturas de pago de Yape */}
      {mostrarModalCapturaYape && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-purple-600" />
                Capturas de pago Yape
              </h3>
              <button onClick={() => setMostrarModalCapturaYape(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg py-8 cursor-pointer hover:bg-purple-50">
              <Smartphone className="w-8 h-8 text-purple-400 mb-2" />
              <span className="text-sm text-purple-700 font-medium">Click para elegir captura(s) de pantalla</span>
              <span className="text-xs text-gray-500 mt-1">Podés seleccionar varias a la vez</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { agregarCapturasYape(e.target.files); e.target.value = ''; }}
              />
            </label>

            {capturasYape.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {capturasYape.map((captura, i) => (
                  <div key={i} className="relative">
                    <img src={captura} alt={`Captura ${i + 1}`} className="w-full h-24 object-cover rounded border" />
                    <button
                      type="button"
                      onClick={() => quitarCapturaYape(i)}
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setMostrarModalCapturaYape(false)}
              className="w-full mt-6 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Listo ({capturasYape.length} captura{capturasYape.length === 1 ? '' : 's'})
            </button>
          </div>
        </div>
      )}

      {/* Modal de comprobante de pago desde el Historial: sirve para adjuntar la captura
          de Yape (u otra evidencia de pago) despues de la venta, ya que ahora es opcional
          y no bloquea el cierre de la venta. */}
      {ventaComprobante && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-purple-600" />
                Comprobante de pago — {ventaComprobante.numero}
              </h3>
              <button
                onClick={() => { setVentaComprobante(null); setCapturasNuevasComprobante([]); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {Array.isArray(ventaComprobante.yape_capturas) && ventaComprobante.yape_capturas.length > 0 && (
              <div className="mb-4">
                <p className="text-xs text-gray-500 mb-2">Ya adjuntas:</p>
                <div className="grid grid-cols-3 gap-2">
                  {ventaComprobante.yape_capturas.map((url: string, i: number) => (
                    <a key={i} href={`${import.meta.env.VITE_API_URL || 'http://localhost:8787'}${url}`} target="_blank" rel="noreferrer">
                      <img
                        src={`${import.meta.env.VITE_API_URL || 'http://localhost:8787'}${url}`}
                        alt={`Comprobante ${i + 1}`}
                        className="w-full h-24 object-cover rounded border"
                      />
                    </a>
                  ))}
                </div>
              </div>
            )}

            <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-lg py-8 cursor-pointer hover:bg-purple-50">
              <Smartphone className="w-8 h-8 text-purple-400 mb-2" />
              <span className="text-sm text-purple-700 font-medium">Click para elegir captura(s) de pantalla</span>
              <span className="text-xs text-gray-500 mt-1">Es opcional, no es obligatorio para cerrar la venta</span>
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { agregarCapturasComprobante(e.target.files); e.target.value = ''; }}
              />
            </label>

            {capturasNuevasComprobante.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-4">
                {capturasNuevasComprobante.map((captura, i) => (
                  <div key={i} className="relative">
                    <img src={captura} alt={`Nueva captura ${i + 1}`} className="w-full h-24 object-cover rounded border" />
                    <button
                      type="button"
                      onClick={() => quitarCapturaNuevaComprobante(i)}
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 mt-6">
              <button
                onClick={() => { setVentaComprobante(null); setCapturasNuevasComprobante([]); }}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cerrar
              </button>
              <button
                onClick={subirComprobante}
                disabled={capturasNuevasComprobante.length === 0 || subiendoComprobante}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {subiendoComprobante ? 'Subiendo...' : `Subir (${capturasNuevasComprobante.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cambiar Tipo de Pago (contado <-> credito), con clave de administrador */}
      {ventaCambiarTipo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Lock className="w-5 h-5 text-cyan-600" />
                Cambiar tipo de pago — {ventaCambiarTipo.numero}
              </h3>
              <button onClick={() => setVentaCambiarTipo(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Tipo actual: <strong>{ventaCambiarTipo.tipo_pago === 'credito' ? 'Crédito' : ventaCambiarTipo.tipo_pago === 'mixto' ? 'Mixto' : ventaCambiarTipo.tipo_pago === 'yape' ? 'Yape' : 'Contado'}</strong> — Total: <strong>S/ {Number(ventaCambiarTipo.total).toFixed(2)}</strong>
            </p>

            <label className="block text-sm font-medium mb-1">Nuevo tipo de pago</label>
            <select
              value={nuevoTipoPago}
              onChange={(e) => setNuevoTipoPago(e.target.value as 'efectivo' | 'credito' | 'yape')}
              className="w-full p-2 border rounded-lg mb-3"
            >
              <option value="efectivo">Contado (efectivo)</option>
              <option value="yape">Yape</option>
              <option value="credito">Crédito</option>
            </select>

            {nuevoTipoPago === 'credito' && (
              <>
                <label className="block text-sm font-medium mb-1">Bus / Padrón</label>
                <BusSelect buses={buses} value={padronCambioTipo} onChange={(v) => setPadronCambioTipo(v)} className="w-full p-2 border rounded-lg mb-3" />
              </>
            )}

            {!ventaCambiarTipo.cerrada && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 mb-3">
                Esta venta sigue <strong>abierta</strong> (pieza pendiente) — todavía no pasó por Caja Central,
                así que este cambio solo ajusta la deuda del bus, sin tocar ningún saldo de caja.
              </div>
            )}

            <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 text-xs text-cyan-800 mb-3">
              {!ventaCambiarTipo.cerrada
                ? (nuevoTipoPago === 'credito' ? 'Se suma a la deuda del bus.' : 'Se resta de la deuda del bus (si ya se pagó parte, puede no alcanzar).')
                : nuevoTipoPago === 'credito'
                ? 'Se revierte el monto de Caja Central (queda registrado como corrección) y se suma a la deuda del bus.'
                : 'Se resta de la deuda del bus (si ya se pagó parte, puede no alcanzar) y se registra como ingreso en Caja Central.'}
            </div>

            <label className="block text-sm font-medium mb-1">Clave de administrador</label>
            <input
              type="password"
              value={claveCambioTipo}
              onChange={(e) => setClaveCambioTipo(e.target.value)}
              className="w-full p-2 border rounded-lg mb-4"
              placeholder="Clave"
            />

            <div className="flex gap-2">
              <button
                onClick={() => setVentaCambiarTipo(null)}
                disabled={enviandoCambioTipo}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarCambioTipo}
                disabled={enviandoCambioTipo}
                className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50"
              >
                {enviandoCambioTipo ? 'Guardando...' : 'Confirmar cambio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cambiar Bus (se eligio el bus equivocado al vender/asignar) */}
      {ventaCambiarBus && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Bus className="w-5 h-5 text-teal-600" />
                Cambiar bus — {ventaCambiarBus.numero}
              </h3>
              <button onClick={() => setVentaCambiarBus(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Bus actual: <strong>{ventaCambiarBus.placa_bus || 'sin padrón'}</strong> — Total: <strong>S/ {Number(ventaCambiarBus.total).toFixed(2)}</strong>
            </p>

            <label className="block text-sm font-medium mb-1">Bus destino</label>
            <BusSelect buses={buses.filter(b => b.padron !== ventaCambiarBus.placa_bus)} value={nuevoBusCambio} onChange={(v) => setNuevoBusCambio(v)} className="w-full p-2 border rounded-lg mb-3" />

            <div className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-xs text-teal-800 mb-3">
              {(Number(ventaCambiarBus.saldo_credito_productos) || 0) + (Number(ventaCambiarBus.saldo_credito_servicios) || 0) > 0.005
                ? 'Esta venta todavía tiene saldo de crédito pendiente: ese saldo se resta de la deuda del bus actual y se suma a la del bus destino.'
                : 'Esta venta ya está pagada (sin saldo pendiente): solo se corrige el bus, no se toca Caja Central ni ninguna deuda.'}
            </div>

            <label className="block text-sm font-medium mb-1">Clave de administrador</label>
            <input
              type="password"
              value={claveCambioBus}
              onChange={(e) => setClaveCambioBus(e.target.value)}
              className="w-full p-2 border rounded-lg mb-4"
              placeholder="Clave"
            />

            <div className="flex gap-2">
              <button
                onClick={() => setVentaCambiarBus(null)}
                disabled={enviandoCambioBus}
                className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={confirmarCambioBus}
                disabled={enviandoCambioBus}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {enviandoCambioBus ? 'Guardando...' : 'Confirmar cambio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Asignar Carrito a Bus (piezas pendientes, sin imprimir) */}
      {mostrarAsignarBus && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
          <div className="bg-white rounded-lg p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
              <Bus className="text-teal-600" size={20} />
              Asignar carrito a un bus
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Las {carritoItems.length} pieza(s) del carrito quedarán pendientes para este bus, sin imprimir, hasta que se cierren desde "Piezas Usadas" en Buses.
            </p>
            <label className="block text-sm font-medium mb-2">
              Bus / Padrón <span className="text-red-500">*</span>
            </label>
            <BusSelect buses={buses} value={busAsignacion} onChange={(v) => setBusAsignacion(v)} className="w-full p-2 border rounded mb-6" />

            <label className="block text-sm font-medium mb-2">
              Tipo de pago <span className="text-red-500">*</span>
            </label>
            <select
              value={tipoPagoAsignacion}
              onChange={(e) => setTipoPagoAsignacion(e.target.value as 'credito' | 'efectivo')}
              className="w-full p-2 border rounded mb-2"
            >
              <option value="credito">A crédito del bus</option>
              <option value="efectivo">Al contado (se cobra ahora)</option>
            </select>
            <div className="bg-teal-50 border border-teal-200 rounded-lg px-3 py-2 text-xs text-teal-800 mb-6">
              {tipoPagoAsignacion === 'credito'
                ? 'Se suma a la deuda del bus apenas se asigna, aunque la pieza siga pendiente de cerrar.'
                : 'No se suma a la deuda del bus. Entra a Caja Central de inmediato, porque ya se cobró ahora.'}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setMostrarAsignarBus(false)}
                disabled={asignando}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={asignarCarritoABus}
                disabled={asignando}
                className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50"
              >
                {asignando ? 'Asignando...' : 'Confirmar Asignación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Cliente */}
      {ventaParaImprimir && <TicketImprimible venta={ventaParaImprimir} />}

      {precioEditando && (
        <CambiarPrecioModal
          nombre={precioEditando.nombre}
          precioLista={Number(precioEditando.precio_lista ?? precioEditando.precio) || 0}
          precioActual={Number(precioEditando.precio) || 0}
          onClose={() => setPrecioEditando(null)}
          onAplicar={(precio, aut) => {
            setCarritoItems(prev => prev.map(i => (i.id === precioEditando.id ? { ...i, precio } : i)));
            if (aut) setAutorizacionPrecio(aut);
            setPrecioEditando(null);
          }}
        />
      )}
      {autorizandoDescuento && (
        <CambiarPrecioModal
          soloAutorizar
          nombre={`Descuento de S/ ${ventaActual.descuento.toFixed(2)}`}
          precioLista={0}
          precioActual={0}
          onClose={() => setAutorizandoDescuento(false)}
          onAplicar={(_p, aut) => {
            if (aut) setAutorizacionPrecio(aut);
            setAutorizandoDescuento(false);
            toast.success('Descuento autorizado. Vuelve a presionar para procesar la venta.');
          }}
        />
      )}

      {mostrarVentaRapida && (
        <VentaRapidaModal
          onClose={() => setMostrarVentaRapida(false)}
          onCompletada={() => {
            cargarProductos();
            cargarVentasDesdeSupabase();
          }}
        />
      )}

      {ventaParaAnular && (
        <AnularModal
          titulo={`Anular venta ${ventaParaAnular.numero}`}
          descripcion={`Se anulará la venta ${ventaParaAnular.numero} de S/ ${Number(ventaParaAnular.total).toFixed(2)}${ventaParaAnular.placa_bus ? ` (bus ${ventaParaAnular.placa_bus})` : ''}. Se devuelve el stock, se quita la deuda del bus si fue a crédito y se saca de caja lo cobrado. Después registra la venta correcta. Si la venta ya tiene pagos de crédito, primero anula esos pagos en Créditos.`}
          textoBoton="Anular venta"
          onClose={() => setVentaParaAnular(null)}
          onConfirmar={async (clave, motivo) => {
            const r = await ventasApi.anular(ventaParaAnular.id, { clave, motivo });
            toast.success(`Venta ${r.numero} anulada`);
            setVentaParaAnular(null);
            cargarProductos();
            cargarVentasDesdeSupabase();
          }}
        />
      )}

      {busPagoCredito && (
        <PagarCreditoModal
          bus={busPagoCredito}
          onClose={() => setBusPagoCredito(null)}
          onPagado={(padron, nuevoSaldo) => {
            setBuses(prev => prev.map(b => b.padron === padron ? { ...b, credito_usado: nuevoSaldo } : b));
          }}
        />
      )}
    </div>
  );
};

export default VentasCompleto;